import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import blogService from "../../api/blogService";
import logoDark from "../../assets/images/logo-dark.svg";
import { useTheme } from "../../context/ThemeContext";
import "./Unsubscribe.scss";

// Public unsubscribe landing page.
//
// Entry points feed this page:
//  1. The one-click unsubscribe link in campaign emails carries ?token=... —
//     this page calls the backend itself (via the web app's own API client)
//     to record the unsubscribe and get back the result, rather than the
//     backend recording it and redirecting here directly. That's deliberate:
//     it means only FRONTEND_URL needs to be a reachable address for the
//     link to work, since the backend is never linked to directly from an
//     email — the browser reaches it the same way the rest of the web app does.
//  2. ?status=success|invalid|error directly (no token) is also still
//     supported as a fallback, in case anything ever redirects here itself.
//  3. Non-signup users can type their email to unsubscribe from newsletters,
//     which calls the existing POST /public/blog-newsletter/unsubscribe.

const MailOffIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 5H10a2 2 0 0 0-2 2v2" />
    <path d="m2 2 20 20" />
    <path d="M22 8.6V18a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V7" />
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L7 10" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const AlertIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const Unsubscribe = () => {
  const { resolvedTheme } = useTheme();
  const logoSrc =
    resolvedTheme === "dark" ? logoDark : process.env.PUBLIC_URL + "/logo.svg";
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [loading, setLoading] = useState(!!token);
  const [fetchedStatus, setFetchedStatus] = useState(null);
  const [fetchedCategory, setFetchedCategory] = useState(null);

  useEffect(() => {
    if (!token) return;

    let cancelled = false;
    blogService
      .unsubscribeByToken(token)
      .then((res) => {
        if (cancelled) return;
        const body = res?.data?.data || {};
        setFetchedStatus(body.status || "invalid");
        setFetchedCategory(body.category || "");
      })
      .catch((err) => {
        console.error("Unsubscribe failed:", err?.response?.status, err?.response?.data || err?.message);
        if (!cancelled) setFetchedStatus("error");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  const status = token ? fetchedStatus : searchParams.get("status");
  const category = token ? fetchedCategory : searchParams.get("category");

  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleManualUnsubscribe = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      toast.error("Please enter your email address");
      return;
    }

    setSubmitting(true);
    try {
      // Pass the category from the URL (if the user arrived from an email link)
      // so the backend turns off the right preference for registered users.
      const res = await blogService.unsubscribeNewsletter({
        email: trimmed,
        category: category || undefined,
      });
      toast.success(
        res?.data?.message || "You've been unsubscribed. We're sorry to see you go!",
      );
      setEmail("");
    } catch (err) {
      // Validation errors put the real message in `data` (a string) and a
      // generic label in `message`; business errors put it in `message`.
      const body = err?.response?.data;
      const msg =
        (typeof body?.data === "string" && body.data) ||
        body?.message ||
        "We couldn't process your request. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Loading state (fetching the token-based unsubscribe result) ──────
  if (loading) {
    return (
      <div className="unsubscribe-page">
        <div className="unsubscribe-card">
          <div className="unsubscribe-card__header">
            <img
              src={logoSrc}
              alt="MapMyPay"
              className="unsub-logo"
            />
          </div>
          <div className="unsubscribe-card__body">
            <h1 className="unsub-title">Just a moment…</h1>
            <p className="unsub-subtitle">Processing your request.</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Success view (arrived from a valid one-click email link) ──────
  if (status === "success") {
    return (
      <div className="unsubscribe-page">
        <div className="unsubscribe-card">
          <div className="unsubscribe-card__header">
            <img
              src={logoSrc}
              alt="MapMyPay"
              className="unsub-logo"
            />
            <div className="unsub-badge is-success">
              <CheckIcon />
            </div>
          </div>

          <div className="unsubscribe-card__body">
            <div className="unsub-success">
              <h1 className="unsub-title">You're all set</h1>
              <p className="unsub-subtitle">
                You've been unsubscribed and will no longer receive{" "}
                {category ? <strong>{category}</strong> : "these"} emails from
                MapMyPay. Thanks for giving us a try — you're always welcome
                back whenever you need us.
              </p>
              <a className="unsub-return" href={process.env.PUBLIC_URL + "/"}>
                Return to MapMyPay
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Form view (manual unsubscribe, optionally with a link-error banner) ──
  return (
    <div className="unsubscribe-page">
      <div className="unsubscribe-card">
        <div className="unsubscribe-card__header">
          <img
            src={logoSrc}
            alt="MapMyPay"
            className="unsub-logo"
          />
          <div className="unsub-badge">
            <MailOffIcon />
          </div>
        </div>

        <div className="unsubscribe-card__body">
          {status === "invalid" && (
            <div className="unsub-alert is-warning">
              <AlertIcon />
              <span>
                This unsubscribe link is invalid or has expired. No problem —
                you can unsubscribe manually using the form below.
              </span>
            </div>
          )}
          {status === "error" && (
            <div className="unsub-alert is-error">
              <AlertIcon />
              <span>
                Something went wrong while processing that link. Please
                unsubscribe using the form below instead.
              </span>
            </div>
          )}

          <h1 className="unsub-title">We're sorry to see you go</h1>
          <p className="unsub-subtitle">
            Enter your email address and we'll stop sending you newsletters and
            updates. You can resubscribe any time — no hard feelings.
          </p>

          <form className="unsub-form" onSubmit={handleManualUnsubscribe}>
            <label className="unsub-label" htmlFor="unsub-email">
              Email address
            </label>
            <input
              id="unsub-email"
              type="email"
              className="unsub-input"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting}
              required
            />

            <button type="submit" className="unsub-submit" disabled={submitting}>
              {submitting ? "Unsubscribing…" : "Unsubscribe me"}
            </button>
          </form>

          <div className="unsub-note">
            <strong>Have a MapMyPay account?</strong> You can choose exactly
            which emails you receive — job alerts, community digests, and more —
            from your profile's notification settings, without unsubscribing
            from everything.
          </div>
        </div>
      </div>
    </div>
  );
};

export default Unsubscribe;
